// Per-exercise progress signal for the Analysis tab. Reads finished sessions and
// classifies each exercise as moving up, flat, down, or stalled, reusing the same
// est-1RM session score the engine's stall detector uses (so "stalled" here matches
// the engine's stall flag exactly). Pure: takes sessions + an exercise lookup.

import type { Exercise, GymId, MuscleGroup, Session } from './types';
import { sessionScore } from './estimate';

export type Trend = 'up' | 'flat' | 'down' | 'stalled' | 'new';

export interface ExerciseInsight {
  exerciseId: string;
  name: string;
  muscle: MuscleGroup;
  sessionCount: number;
  lastDate: string;
  // Latest session's score vs the previous one, as a percent (null with <2 sessions).
  deltaPct: number | null;
  trend: Trend;
}

// scores newest-first. Mirrors progression.detectStall: no improvement of the
// newest score over the two prior is a stall (needs >= 3 sessions).
function classify(scores: number[]): Trend {
  const n = scores.length;
  if (n < 2) return 'new';
  if (n >= 3) {
    const [a, b, c] = scores;
    const progressed = a > b + 1e-6 || b > c + 1e-6;
    if (!progressed) return 'stalled';
  }
  const [latest, prev] = scores;
  if (latest > prev * 1.01) return 'up';
  if (latest < prev * 0.99) return 'down';
  return 'flat';
}

// `gymId` scopes the scores to one gym and must be passed for the trend to mean
// anything: this reuses the engine's stall rule, and two gyms' loading scales
// alternating in one score series reads as oscillation, flagging "stalled" or
// "down" on noise — and contradicting the session screen's own stall flag for the
// same exercise. Omit it only for a genuinely cross-gym view.
export function buildInsights(
  sessions: Session[],
  exById: Map<string, Exercise>,
  gymId?: GymId | null,
): ExerciseInsight[] {
  // sessions are finished and newest-first, so scores accumulate newest-first.
  const byEx = new Map<string, { scores: number[]; lastDate: string }>();
  for (const s of sessions) {
    if (gymId != null && s.gymId !== gymId) continue;
    for (const entry of s.entries) {
      const ex = exById.get(entry.exerciseId);
      if (!ex) continue;
      const score = sessionScore(entry.sets, ex, s.bodyweightKg);
      if (score <= 0) continue;
      const rec = byEx.get(entry.exerciseId);
      if (rec) rec.scores.push(score);
      else byEx.set(entry.exerciseId, { scores: [score], lastDate: s.date });
    }
  }

  const out: ExerciseInsight[] = [];
  for (const [exerciseId, { scores, lastDate }] of byEx) {
    const ex = exById.get(exerciseId);
    if (!ex) continue;
    const deltaPct = scores.length >= 2 && scores[1] > 0 ? ((scores[0] - scores[1]) / scores[1]) * 100 : null;
    out.push({
      exerciseId,
      name: ex.name,
      muscle: ex.muscleGroup,
      sessionCount: scores.length,
      lastDate,
      deltaPct,
      trend: classify(scores),
    });
  }
  out.sort((a, b) => (a.lastDate < b.lastDate ? 1 : -1));
  return out;
}
